import { 
  SupportProblem, 
  SupportSolution, 
  SupportTicket,
  AIResolutionAttempt 
} from '@/types/support';
import { knowledgeBase } from './knowledge-base';

export class ResolutionService {
  private problems = knowledgeBase.problems;
  public solutions = knowledgeBase.solutions; // Torna público para acesso externo
  private categories = knowledgeBase.categories;

  /**
   * Analisa o ticket e tenta encontrar uma solução automatizada
   */
  async analyzeTicket(ticket: SupportTicket): Promise<AIResolutionAttempt> {
    
    // Encontra o problema mais relevante
    const matchedProblem = this.findBestMatchingProblem(ticket);
    
    if (!matchedProblem) {
      return {
        ticketId: ticket.id || 'temp-' + Date.now(),
        confidenceScore: 0,
        timestamp: new Date()
      };
    }

    // Busca a solução para o problema encontrado
    const solution = this.findSolutionForProblem(matchedProblem.problem.id);
    
    return {
      ticketId: ticket.id || 'temp-' + Date.now(),
      problemMatched: matchedProblem.problem.id,
      solutionOffered: solution?.id,
      confidenceScore: matchedProblem.score,
      timestamp: new Date()
    };
  }

  /**
   * Encontra o problema que melhor corresponde ao ticket
   */
  private findBestMatchingProblem(ticket: SupportTicket): { problem: SupportProblem, score: number } | null {
    const descriptionLower = ticket.description.toLowerCase();
    const issueLower = ticket.issue.toLowerCase();
    const categoryLower = ticket.main.toLowerCase();
    
    let bestMatch: { problem: SupportProblem, score: number } | null = null;
    let highestScore = 0;

    for (const problem of this.problems) {
      let score = 0;
      
      // Verifica compatibilidade com newsletter
      if (problem.newsletter && problem.newsletter.length > 0) {
        if (!problem.newsletter.includes('all') && !problem.newsletter.includes(ticket.newsletter)) {
          continue; // Pula problemas não aplicáveis a esta newsletter
        }
      }

      // Pontuação por categoria
      const category = this.categories.find(c => c.id === problem.categoryId);
      if (category) {
        if (categoryLower.includes(category.name.toLowerCase()) || 
            category.keywords.some(k => categoryLower.includes(k))) {
          score += 30;
        }
      }

      // Pontuação por issue exato
      if (problem.title.toLowerCase() === issueLower) {
        score += 50;
      } else if (problem.title.toLowerCase().includes(issueLower) || 
                 issueLower.includes(problem.title.toLowerCase())) {
        score += 25;
      }

      // Pontuação por palavras-chave na descrição
      for (const keyword of problem.keywords) {
        if (descriptionLower.includes(keyword.toLowerCase())) {
          score += 10;
        }
      }

      // Pontuação por frequência (problemas mais comuns têm prioridade)
      score += Math.min(problem.frequency / 100, 10); // Max 10 pontos por frequência

      // Análise de similaridade textual simples
      const problemWords = problem.description.toLowerCase().split(' ');
      const descriptionWords = descriptionLower.split(' ');
      const commonWords = problemWords.filter(word => 
        descriptionWords.includes(word) && word.length > 3
      );
      score += commonWords.length * 2;

      if (score > highestScore) {
        highestScore = score;
        bestMatch = { problem, score: Math.min(score / 100, 1) }; // Normaliza para 0-1
      }
    }

    // Só retorna se a confiança for maior que 40%
    return bestMatch && bestMatch.score > 0.4 ? bestMatch : null;
  }

  /**
   * Busca a solução para um problema específico
   */
  findSolutionForProblem(problemId: string): SupportSolution | null {
    return this.solutions.find(s => s.problemId === problemId) || null;
  }

  /**
   * Verifica se deve oferecer solução automatizada
   */
  shouldOfferAutomatedSolution(attempt: AIResolutionAttempt): boolean {
    // Oferece solução se:
    // 1. Tem alta confiança (> 60%)
    // 2. Tem uma solução disponível
    // 3. A solução tem alta taxa de sucesso (> 70%)
    
    if (attempt.confidenceScore < 0.6) return false;
    if (!attempt.solutionOffered) return false;
    
    const solution = this.solutions.find(s => s.id === attempt.solutionOffered);
    if (!solution) return false;
    
    return solution.successRate > 0.7;
  }

  /**
   * Processa feedback do usuário sobre a solução
   */
  async processFeedback(
    attemptId: string, 
    feedback: 'helpful' | 'not_helpful',
    createTicket: boolean = false
  ): Promise<void> {
    // Aqui você poderia:
    // 1. Salvar o feedback em um banco de dados
    // 2. Atualizar métricas de sucesso da solução
    // 3. Criar ticket para suporte humano se necessário
    
    console.log(`Feedback recebido: ${feedback} para tentativa ${attemptId}`);
    
    if (feedback === 'not_helpful' && createTicket) {
      // Lógica para criar ticket para suporte humano
      console.log('Criando ticket para suporte humano...');
    }
  }

  /**
   * Busca soluções relacionadas
   */
  findRelatedSolutions(solutionId: string, limit: number = 3): SupportSolution[] {
    const solution = this.solutions.find(s => s.id === solutionId);
    if (!solution) return [];
    
    const problem = this.problems.find(p => p.id === solution.problemId);
    if (!problem) return [];
    
    // Busca outros problemas da mesma categoria
    const relatedProblems = this.problems.filter(p => 
      p.categoryId === problem.categoryId && p.id !== problem.id
    );
    
    // Retorna soluções para problemas relacionados
    return relatedProblems
      .map(p => this.solutions.find(s => s.problemId === p.id))
      .filter(s => s !== undefined)
      .slice(0, limit) as SupportSolution[];
  }

  /**
   * Gera estatísticas de resolução
   */
  getResolutionStats() {
    const totalProblems = this.problems.reduce((sum, p) => sum + p.frequency, 0);
    const problemsWithSolutions = this.problems.filter(p => 
      this.solutions.some(s => s.problemId === p.id)
    );
    const avgSuccessRate = this.solutions.reduce((sum, s) => sum + s.successRate, 0) / this.solutions.length;
    
    return {
      totalProblems,
      problemsWithSolutions: problemsWithSolutions.length,
      totalSolutions: this.solutions.length,
      averageSuccessRate: avgSuccessRate,
      coverageRate: problemsWithSolutions.length / this.problems.length
    };
  }
}

// Singleton instance
export const resolutionService = new ResolutionService();